import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    IndianRupee, Building2, Loader2, AlertCircle,
    RotateCcw, Send, ChevronDown,
    ArrowRight, CheckCircle,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';

// CC lists + balance from cashVoucherSlice
import {
    fetchSelfCCList,
    fetchOtherCCList,
    fetchCCBalance,
    resetCashVoucher,
    selectSelfCCList,
    selectOtherCCList,
    selectCCBalance,
    selectSelfCCLoading,
    selectOtherCCLoading,
    selectCCBalanceLoading,
} from '../../slices/accountsSlice/cashVoucherSlice';

// Save from ccCashTransferSlice
import {
    saveCCCashTransfer,
    clearSaveResult,
    resetCCCashTransfer,
    selectCCSaveStatus,
    selectCCSaveError,
    selectCCSaveLoading,
} from '../../slices/accountsSlice/ccCashTransferSlice';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return '0.00';
    return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

const Label = ({ children, required }) => (
    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SelectIcon = ({ loading }) => (
    <div className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
        {loading
            ? <Loader2 className="h-4 w-4 text-indigo-500 animate-spin" />
            : <ChevronDown className="h-4 w-4 text-gray-400" />}
    </div>
);

const CardHeader = ({ icon: Icon, title, action }) => (
    <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/60 dark:bg-gray-900/40 rounded-t-xl">
        <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-indigo-500" />
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-100">{title}</h2>
        </div>
        {action}
    </div>
);

// Summary panel row
const SumRow = ({ label, value }) => (
    <div className="flex justify-between gap-3">
        <dt className="text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="font-semibold text-gray-800 dark:text-gray-100 truncate text-right" title={value || ''}>{value || '—'}</dd>
    </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const CCCashTransfer = ({ menuData }) => {
    const dispatch = useDispatch();

    // Auth
    const { userData } = useSelector(s => s.auth);
    const roleId   = userData?.roleId   || userData?.RID  || 0;
    const userId   = userData?.userId   || userData?.UID  || userData?.employeeId || '';
    const userName = userData?.userName || userData?.UserName || 'system';

    // CC lists + balance
    const selfCCList     = useSelector(selectSelfCCList);
    const otherCCList    = useSelector(selectOtherCCList);
    const ccBalance      = useSelector(selectCCBalance);
    const selfCCLoading  = useSelector(selectSelfCCLoading);
    const otherCCLoading = useSelector(selectOtherCCLoading);
    const balanceLoading = useSelector(selectCCBalanceLoading);

    // Save
    const saveStatus  = useSelector(selectCCSaveStatus);
    const saveError   = useSelector(selectCCSaveError);
    const saveLoading = useSelector(selectCCSaveLoading);

    // Form state
    const [selfCC,       setSelfCC]       = useState('');
    const [transferCC,   setTransferCC]   = useState('');
    const [amount,       setAmount]       = useState('');
    const [transferDate, setTransferDate] = useState(null);
    const [remarks,      setRemarks]      = useState('');

    // ── Init ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId && roleId) {
            dispatch(fetchSelfCCList({ uid: userId, rid: roleId }));
        }
        return () => {
            dispatch(resetCashVoucher());
            dispatch(resetCCCashTransfer());
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, userId, roleId]);

    // ── Fetch balance when self CC changes ──────────────────────────────────
    useEffect(() => {
        if (selfCC) {
            dispatch(fetchCCBalance(selfCC));
            // Legacy GetAllCashCC(cc = selected self CC): the list excludes the source CC itself.
            dispatch(fetchOtherCCList({ id: selfCC, type: '' }));
            setTransferCC('');
            setAmount('');
        }
    }, [selfCC, dispatch]);

    // ── Handle save result ──────────────────────────────────────────────────
    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('CC Cash Transfer saved successfully!');
            dispatch(clearSaveResult());
            handleReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus, saveError, dispatch]);

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleReset = () => {
        setSelfCC('');
        setTransferCC('');
        setAmount('');
        setTransferDate(null);
        setRemarks('');
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^\d*\.?\d*$/.test(val)) setAmount(val);
    };

    const handleSubmit = () => {
        if (!selfCC)       { toast.warn('Please select Self Cost Center.');    return; }
        if (!transferCC)   { toast.warn('Please select Transfer Cost Center.'); return; }
        if (!amount || parseFloat(amount) <= 0) { toast.warn('Please enter a valid amount.'); return; }
        if (!transferDate) { toast.warn('Please select Transfer Date.');        return; }

        const numAmount = parseFloat(amount);
        const balance   = parseFloat(ccBalance) || 0;
        if (numAmount > balance) {
            toast.warn(`Amount ₹${fmt(numAmount)} exceeds available balance ₹${fmt(balance)}.`);
            return;
        }

        const txnDate = new Date(transferDate)
            .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            .replace(/ /g, '-');

        dispatch(saveCCCashTransfer({
            GPSessionCCCode:    selfCC,
            CashTransferCCCode: transferCC,
            CCTransferDate:     txnDate,
            CCTransferRemarks:  remarks,
            CCTransferAmount:   numAmount,
            RoleID:             roleId,
            Createdby:          userName,
        }));
    };

    // ── Derived ─────────────────────────────────────────────────────────────
    const filteredOtherCC   = otherCCList.filter(cc => cc.CC_Code?.split(',')[0].trim() !== selfCC);
    const selfCCLabel       = selfCC     ? selfCCList.find(c => c.CC_Code?.split(',')[0].trim() === selfCC)?.CC_Code    || selfCC     : '—';
    const transferCCLabel   = transferCC ? otherCCList.find(c => c.CC_Code?.split(',')[0].trim() === transferCC)?.CC_Code || transferCC : '—';
    const numAmount         = parseFloat(amount) || 0;
    const balance           = parseFloat(ccBalance) || 0;
    const exceedsBalance    = selfCC && ccBalance != null && numAmount > balance;
    const canSubmit         = selfCC && transferCC && numAmount > 0 && transferDate && !exceedsBalance && !saveLoading;
    const txnDateDisplay    = transferDate
        ? new Date(transferDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')
        : '—';
    const isBusy = saveLoading || selfCCLoading || otherCCLoading;

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-3 md:p-4">

            {/* ── Page Header (slim) ───────────────────────────────────────── */}
            <div className="max-w-7xl mx-auto mb-4">
                <div className="rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-md shadow-indigo-500/20 px-5 py-3 text-white flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <ArrowRight className="h-5 w-5 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-lg font-black tracking-tight leading-tight truncate">
                            {menuData?.name || 'CC Cash Transfer'}
                        </h1>
                        <p className="text-indigo-200 text-xs">Accounts Module · Transfer cash balance between cost centers</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">

                {/* ── Left: single form card ───────────────────────────────── */}
                <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <CardHeader icon={Building2} title="Transfer Details" />
                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">

                        {/* Self CC */}
                        <div>
                            <Label required>Self CC (From)</Label>
                            <div className="relative">
                                <select
                                    value={selfCC}
                                    onChange={e => setSelfCC(e.target.value)}
                                    disabled={selfCCLoading || isBusy}
                                    className={`${inputCls} appearance-none pr-8`}
                                >
                                    <option value="">{selfCCLoading ? 'Loading…' : '— Select Self CC —'}</option>
                                    {selfCCList.map(cc => (
                                        <option key={cc.CC_Id} value={cc.CC_Code?.split(',')[0].trim()}>{cc.CC_Code}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={selfCCLoading} />
                            </div>
                            {selfCC && (
                                <p className="mt-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                                    Available:{' '}
                                    {balanceLoading
                                        ? <><Loader2 className="h-3 w-3 animate-spin" /> Loading…</>
                                        : <>₹{fmt(ccBalance)}</>}
                                </p>
                            )}
                        </div>

                        {/* Transfer CC */}
                        <div>
                            <Label required>Transfer CC (To)</Label>
                            <div className="relative">
                                <select
                                    value={transferCC}
                                    onChange={e => setTransferCC(e.target.value)}
                                    disabled={!selfCC || otherCCLoading || isBusy}
                                    className={`${inputCls} appearance-none pr-8`}
                                >
                                    <option value="">
                                        {!selfCC ? 'Select Self CC first' : otherCCLoading ? 'Loading…' : '— Select Transfer CC —'}
                                    </option>
                                    {filteredOtherCC.map(cc => (
                                        <option key={cc.CC_Id} value={cc.CC_Code?.split(',')[0].trim()}>{cc.CC_Code}</option>
                                    ))}
                                </select>
                                <SelectIcon loading={otherCCLoading} />
                            </div>
                        </div>

                        {/* Date */}
                        <div>
                            <Label required>Transfer Date</Label>
                            <CustomDatePicker
                                value={transferDate}
                                onChange={setTransferDate}
                                disabled={isBusy}
                                maxDate={new Date()}
                            />
                        </div>

                        {/* Amount */}
                        <div>
                            <Label required>Transfer Amount (₹)</Label>
                            <div className="relative">
                                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    disabled={!selfCC || isBusy}
                                    className={`${inputCls} pl-8 ${exceedsBalance ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                                />
                            </div>
                            {exceedsBalance && (
                                <p className="text-[11px] text-rose-500 mt-1 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" /> Exceeds balance of ₹{fmt(balance)}
                                </p>
                            )}
                        </div>

                        {/* Remarks */}
                        <div className="sm:col-span-1 lg:col-span-2">
                            <Label>Remarks</Label>
                            <textarea
                                rows={1}
                                placeholder="Add remarks (optional)…"
                                value={remarks}
                                onChange={e => setRemarks(e.target.value)}
                                disabled={isBusy}
                                className={`${inputCls} resize-y`}
                            />
                        </div>
                    </div>
                </div>

                {/* ── Right: sticky summary + actions ──────────────────────── */}
                <div className="lg:sticky lg:top-4">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                        <CardHeader icon={CheckCircle} title="Transfer Summary" />
                        <div className="p-4 space-y-3">
                            <dl className="space-y-1.5 text-xs">
                                <SumRow label="From CC"    value={selfCC ? selfCCLabel : ''} />
                                <SumRow label="Available"  value={selfCC ? (balanceLoading ? 'Loading…' : `₹${fmt(ccBalance)}`) : ''} />
                                <SumRow label="To CC"      value={transferCC ? transferCCLabel : ''} />
                                <SumRow label="Date"       value={transferDate ? txnDateDisplay : ''} />
                                <SumRow label="Remarks"    value={remarks} />
                                <SumRow label="Created By" value={userName} />
                            </dl>

                            <div className={`px-4 py-3 rounded-lg text-white bg-gradient-to-r ${exceedsBalance ? 'from-rose-500 to-rose-600' : 'from-indigo-500 to-violet-600'}`}>
                                <p className="text-white/80 text-[10px] font-semibold uppercase tracking-wider">Transfer Amount</p>
                                <p className="text-2xl font-black">₹{fmt(numAmount)}</p>
                                {exceedsBalance && <p className="text-[11px] text-white/90">Exceeds available balance</p>}
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleReset}
                                    disabled={isBusy}
                                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50"
                                >
                                    <RotateCcw className="h-4 w-4" /> Reset
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {saveLoading
                                        ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                                        : <><Send className="h-4 w-4" /> Submit Transfer</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CCCashTransfer;
